import React from "react";
import { format, isBefore, differenceInCalendarDays } from "date-fns";
import { HiDotsVertical } from "react-icons/hi";
import {
  MilestoneDisplay,
  statusColors,
} from "@shared/interfaces/ProgramFiles";

interface EventProps {
  event: MilestoneDisplay;
  index: number;
  msViewStart: Date;
  weeksPerView: number;
  columnWidth: number;
  eventHeight: number;
  onClick?: () => void;
}

const Event: React.FC<EventProps> = ({
  event,
  index,
  msViewStart,
  weeksPerView,
  columnWidth,
  eventHeight,
  onClick,
}) => {
  const clampedStart = isBefore(event.start_date, msViewStart)
    ? msViewStart
    : event.start_date;
  const offsetDays = differenceInCalendarDays(clampedStart, msViewStart);
  const left = offsetDays * (columnWidth / 7);
  const spanDays = differenceInCalendarDays(event.end_date, clampedStart);
  const fullWidth = spanDays * (columnWidth / 7);
  const viewWidth = weeksPerView * columnWidth;
  const isOverflowingRight = left + fullWidth > viewWidth;
  const isOverflowingLeft = isBefore(event.start_date, msViewStart);
  const width = Math.min(fullWidth, viewWidth - left);
  const EVENT_SPACING = 10;

  return (
    <div
      className="absolute"
      style={{
        top: `${index * eventHeight + 35}px`,
        left: `${left}px`,
        width: `${width}px`,
      }}
      onClick={onClick} // Use the provided click handler
    >
      <div className="relative group">
        <div className="absolute shadow-lg rounded-md w-40 z-[21] top-16 p-3 left-1/2 transform -translate-x-1/2 text-[10px] bg-[#fff] text-black opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          <div className="text-xs space-y-1">
            <div className="font-semibold">{event.name}</div>
            <div>{event.description}</div>
            <div>
              <strong>Leader:</strong> {event.responsible_user?.first_name}{" "}
              {event.responsible_user?.last_name}
            </div>
            <div>
              <strong>Start Date:</strong>{" "}
              {format(event.start_date, "MMM d, yyyy")}
            </div>
            <div>
              <strong>End Date:</strong> {format(event.end_date, "MMM d, yyyy")}
            </div>
          </div>
        </div>

        <div
          className="text-white relative cursor-pointer text-[10px] rounded px-2 flex flex-col justify-center overflow-hidden leading-tight"
          style={{
            height: `${eventHeight - EVENT_SPACING}px`,
            backgroundColor: statusColors[event.status ?? "on track"],
            borderRight: isOverflowingRight ? "4px solid white" : undefined,
            boxShadow: [
              isOverflowingRight ? "inset -4px 0 0 0 rgba(0,0,0,0.2)" : "",
              isOverflowingLeft ? "inset 4px 0 0 0 rgba(0,0,0,0.2)" : "",
            ]
              .filter(Boolean)
              .join(", "),
          }}
        >
          <strong className="block text-ellipsis overflow-hidden whitespace-nowrap text-[12px] mb-1">
            {event.name}
          </strong>
          {/** If total events <= 10, show details */}
          {/** We are directly using 10 since events array isn't passed here */}
          <div className="flex justify-between items-center">
            <span className="opacity-90 text-ellipsis overflow-hidden whitespace-nowrap block text-[12px] leading-tight">
              {event.description}
            </span>
            <span className="text-[12px] flex items-center gap-1 text-white/80 text-ellipsis overflow-hidden whitespace-nowrap">
              <span className="text-[12px]">🏁</span>
              {format(event.start_date, "MMM d")} –{" "}
              {format(event.end_date, "MMM d")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Event;
